import React from "react";
import { useTranslation } from "react-i18next";
import DonutStatCard from "../DonutStatCard";
import { getCurrentClientId } from "../../config/villageConfig";

import arrowUpwardIcon from "../../assets/icons/arrow_upward.svg";
import currencyRupeeIcon from "../../assets/icons/currency_rupee.svg";
import flagIcon from "../../assets/icons/flag.svg";
import homeIcon from "../../assets/icons/home.svg";
import waterSvgIcon from "../../assets/icons/water.svg";
import rentIcon from "../../assets/icons/rent.svg";
import lowPriorityIcon from "../../assets/icons/low_priority.svg";
import motherBabeyIcon from "../../assets/icons/mother_babey.svg";
import tribeIcon from "../../assets/icons/tribe.svg";
import disableIcon from "../../assets/icons/disable.svg";
import { formatPercent } from "../../utils/numUtils";

// Client-specific expenditure data (only for clientId 1)
const CLIENT_1_EXPENDITURE_DATA = [
  { title: "पायाभूत सुविधा आणि विकास कार्य", label: "पायाभूत सुविधा आणि विकास कार्य", received_amount: 1000000, spent_amount: 413897, percentage: 53.02, amount: 1000000, color: "#06b6d4" },
  { title: "वेतन आणि भत्ते", label: "वेतन आणि भत्ते", received_amount: 350916, spent_amount: 204701, percentage: 18.61, amount: 350916, color: "#f97316" },
  { title: "आरोग्य सेवा", label: "आरोग्य सेवा", received_amount: 100000, spent_amount: 50000, percentage: 5.30, amount: 100000, color: "#10b981" },
  { title: "शिक्षण", label: "शिक्षण", received_amount: 150000, spent_amount: 100000, percentage: 7.95, amount: 150000, color: "#8b5cf6" },
  { title: "स्वच्छता", label: "स्वच्छता", received_amount: 160000, spent_amount: 50000, percentage: 8.48, amount: 160000, color: "#f43f5e" },
  { title: "इतर", label: "इतर", received_amount: 125000, spent_amount: 25000, percentage: 6.63, amount: 125000, color: "#f59e0b" },
];

// Default empty data for other clients
const DEFAULT_CHART_DATA = [
  { title: "पायाभूत सुविधा आणि विकास कार्य", label: "पायाभूत सुविधा आणि विकास कार्य", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#06b6d4" },
  { title: "वेतन आणि भत्ते", label: "वेतन आणि भत्ते", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#f97316" },
  { title: "आरोग्य सेवा", label: "आरोग्य सेवा", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#10b981" },
  { title: "शिक्षण", label: "शिक्षण", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#8b5cf6" },
  { title: "स्वच्छता", label: "स्वच्छता", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#f43f5e" },
  { title: "इतर", label: "इतर", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#f59e0b" },
];

const ICON_CONFIG = [
  { icon: currencyRupeeIcon, color: "#14B8A6" },
  { icon: flagIcon, color: "#3B82F6" },
  { icon: homeIcon, color: "#6366F1" },
  { icon: waterSvgIcon, color: "#EC4899" },
  { icon: rentIcon, color: "#F59E0B" },
  { icon: lowPriorityIcon, color: "#FACC15" },
  { icon: disableIcon, color: "#774BFF" },      // दिव्यांग (5%)
  { icon: tribeIcon, color: "#14B876" },        // माघस्वर्गीय (15%)
  { icon: motherBabeyIcon, color: "#3B82F6" },  // महिला बाल कल्याण (10%)
];

const formatAmount = (amount = 0, lang = "en") => {
  const formatted = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    Math.round((amount + Number.EPSILON) * 100) / 100
  );
  if (lang === "mr") {
    const digits = "०१२३४५६७८९";
    return "₹" + formatted.replace(/[0-9]/g, (d) => digits[parseInt(d, 10)]);
  }
  return "₹" + formatted;
};

const StatusCard = ({ card, t }) => (
  <div className="bg-white border border-[#DDDDDD] rounded-lg flex flex-col items-start px-4 py-3 gap-2.5">
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center"
      style={{ backgroundColor: card.iconColor }}
    >
      <img src={card.icon} alt="" className="w-6 h-6" style={{ filter: "brightness(0) invert(1)" }} />
    </div>

    <div className="text-xl font-semibold text-[#0A0A0A]">{card.label}</div>

    <div className="w-full flex flex-col gap-1">
      <div className="flex justify-between w-full">
        <span className="text-base font-medium text-[#0A0A0A]">{t("incomeExpenditureUI.received")}</span>
        <span className="text-base font-medium text-[var(--color-primary)]">{card.received}</span>
      </div>
      <div className="flex justify-between w-full">
        <span className="text-base font-medium text-[#0A0A0A]">{t("incomeExpenditureUI.spent")}</span>
        <span className="text-base font-medium text-[#B7131A]">{card.spent}</span>
      </div>
      <div className="flex justify-between w-full">
        <span className="text-base font-medium text-[#0A0A0A]">{t("expenditureUI.remaining")}</span>
        <span className="text-base font-medium text-[var(--color-primary)]">{card.remaining}</span>
      </div>
    </div>
  </div>
);

const Expenditure = ({ data, totalExpense }) => {
  const { t, i18n } = useTranslation();
  const clientId = getCurrentClientId();
  // Use client-specific data only for clientId 1, otherwise use API data or empty defaults
  const defaultData = clientId === 1 ? CLIENT_1_EXPENDITURE_DATA : DEFAULT_CHART_DATA;
  const rawData = data || defaultData;
  console.log("raw data", data)
  const lang = i18n.language === "en" ? "en" : "mr";
  console.log("Raw expenditure data:", rawData);

  // Calculate total for percentage calculation (use spent_amount or received_amount)
  const totalAmount = rawData.reduce((sum, item) => sum + (parseFloat(item.spent_amount || item.received_amount || item.received || item.spent) || 0), 0);
  
  // For client ID 1, use hardcoded total of 1885916
  const displayTotal = clientId === 1 ? 1885916 : (totalExpense || totalAmount);

  // Transform data for DonutStatCard - add colors and calculate percentages
  const chartData = rawData.map((item, index) => {
    const received = parseFloat(item.received_amount || item.received) || 0;
    const spent = parseFloat(item.spent_amount || item.spent) || 0;
    const amountForPercentage = spent || received; // Use spent if available, otherwise received
    const percentage = totalAmount > 0 ? (amountForPercentage / totalAmount) * 100 : 0;
    const config = ICON_CONFIG[index] || ICON_CONFIG[0];

    return {
      ...item,
      value: percentage, // Required by DonutStatCard for donut chart rendering
      color: config.color, // Required by DonutStatCard for segment colors
      received: received, // Ensure numeric value for amount display
      spent: spent, // Ensure numeric value for amount display
      label: item.title || item.label, // Ensure label is present
    };
  });

  console.log("Transformed expenditure chartData with value:", chartData);

  // Build status cards from chartData (includes all categories)
  const statusCards = chartData.map((item, index) => {
    const received = parseFloat(item.received) || 0;
    const spent = parseFloat(item.spent) || 0;
    const remaining = received - spent;
    const config = ICON_CONFIG[index] || ICON_CONFIG[0];
    return {
      label: item.label || (lang === "en" ? item.labelEn : item.labelMr),
      received: formatAmount(received, lang),
      spent: formatAmount(spent, lang),
      remaining: formatAmount(remaining, lang),
      icon: config.icon,
      iconColor: config.color,
    };
  });

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="rounded-md">
        {/* Header */}
        <div
          className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 rounded-md"
          style={{ background: "linear-gradient(135deg, #EC5042 0%, #B7131A 100%)" }}
        >
          <div className="flex items-center gap-3 justify-center sm:justify-start">
            <div className="w-9 h-9 rounded-full flex items-center justify-center">
              <img src={arrowUpwardIcon} alt="" />
            </div>
            <span className="text-white text-xl sm:text-2xl font-semibold">
              {t("incomeExpenditure.expenditure") || "Expenditure"}
            </span>
          </div>
          <span className="text-white font-bold text-lg sm:text-xl text-center sm:text-right">
            {formatAmount(displayTotal, lang)}
          </span>
        </div>

        {/* Content */}
        <div className="bg-white mt-8 rounded-b-md">
          <div className="mb-8">
            <DonutStatCard chartData={chartData} enableAnimation animationDuration={1500} />
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {statusCards.map((item, idx) => {
              const config = ICON_CONFIG[idx] || ICON_CONFIG[0];
              const label = item.label;
              const amount = item.received; // Use formatted received amount from statusCards
              const percentage = formatPercent(chartData[idx]?.value || 0, lang, { decimals: 2 });
              return (
                <div
                  key={idx}
                  className="flex flex-row items-center px-4 py-2 gap-3 w-full bg-white rounded-lg border border-[#DDDDDD]"
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: config.color }}
                  >
                    <img
                      src={config.icon}
                      alt=""
                      className="w-5 h-5 md:w-6 md:h-6"
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  </div>
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <div className="text-sm md:text-base font-semibold text-[#0A0A0A] truncate w-full">
                      {label}
                    </div>
                    <div className="text-base font-medium text-[var(--color-primary)] truncate w-full">
                      {amount}
                    </div>
                    <div className="text-sm md:text-base font-semibold text-[#0A0A0A]">
                      {percentage}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div>
            <h3 className="text-[#0A0A0A] text-3xl max-sm:text-2xl font-semibold">
              {t("incomeExpenditureUI.currentStatus")}
            </h3>



            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {statusCards.map((card, idx) => (
                <StatusCard key={idx} card={card} t={t} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Expenditure;
